import type { CableTrayRunCreateRequest } from "../services/cableTray";
import { downloadRunJson } from "../services/runExport";
import { CableTrayForm } from "../components/CableTrayForm";
import { CableTrayResultPanel } from "../components/CableTrayResultPanel";
import { CableTrayResultSummary } from "../components/CableTrayResultSummary";
import { CableTrayWarningPanel } from "../components/CableTrayWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useCableTray } from "../hooks/useCableTray";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The cable tray study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the SLD link, warnings, traceability, then the detail tables. EOS-12's one
// page: the fill, the load and the derating consistency of one tray route.
export function CableTrayPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useCableTray();
  // The active revision's single-line diagram, for the feeder pickers (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: CableTrayRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-cable-tray-page>
      <header>
        <p>EOS-12 · Cable tray and routing</p>
        <h1>Cable tray and routing</h1>
        <p>
          Check one tray, ladder or wire-mesh route: the fill of the cables on it from their
          declared overall diameters (the area of a bunched multi-layer against a declared limit,
          the width of a single layer against the usable width), the load from their declared mass
          per metre against the tray's declared rating at its span and the support spacing, and
          the consistency of each stored cable study's installation method with the tray
          arrangement, using the validated KES Electrical OS calculation service. Every limit is
          the engineer's declared figure with its source; nothing is assumed.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="cable-tray-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="cable-tray-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <CableTrayForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="cable-tray-results-heading" data-study-results>
          <h2 id="cable-tray-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the tray route…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <CableTrayResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <CableTrayWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <CableTrayResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to check the tray route.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="cable-tray-review-heading">
        <h2 id="cable-tray-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance declaration.
          Confirm the tray manufacturer's usable dimensions and rated load at its span, the cable
          data sheets' overall diameters and masses, the declared fill limit and support spacing
          with their sources, and the linked cable studies before approval or issue. Segregation,
          the cover's thermal effect, the earth continuity, the bend radii and the tray's
          mechanical design are not evaluated here.
        </p>
      </section>
    </main>
  );
}

export default CableTrayPage;
